/**
 * This week, as a timetable.
 *
 * What stood here before was a list of active enrollments — program name, days, date range,
 * one row per seat. It was accurate, and it was also very nearly a screenshot of the Students
 * page, which is where a parent goes when they want that list. Repeating it on the dashboard
 * spent the page's most valuable space restating something rather than answering anything.
 *
 * The grid answers instead: which days does each class meet, which of my children is in it,
 * and how does the week stack up — read off the shape, with no row to parse.
 *
 Rows are one child's enrollment and columns are days, rather than an hour grid with boxes in
 * it. That choice is forced, and it is worth knowing why: a bar spanning Monday to Friday is
 * many columns wide, so when two of them collide there is nowhere to nudge one — sub-columns
 * move a block by a fraction of a *single* column and leave it on top of its neighbour
 * everywhere else. A row each makes the collision impossible and gives every bar the width to
 * carry its own name. The hours move to the left edge, aligned in a column, which is what a
 * paper timetable does anyway.
 *
 * The cost is that duration is no longer proportional — a six-hour block and a one-hour class
 * are the same height, and the hours are read rather than seen.
 *
 * Deliberately no attendance here. These bars are the schedule, which is a plan; whether a
 * child actually arrived is a recorded fact and belongs to the Today card. Painting a check-in
 * onto a scheduled bar would blur the one distinction the rest of this work is built on.
 */

import { Link } from 'react-router-dom';
import { initialsFor, solidTintFor } from '../StudentAvatar';
import { TYPE_ATTRIBUTE_WIDE, TYPE_SECTION_WIDE } from '../typeScale';
import { useLabelParts } from '../../config/useLabels';
import { formatDateDisplay } from '../../utils/date';
import { getTodaySessionDate } from '../../utils/schedule';
import { getProgramTypeColor } from '../../types/program';
import { buildWeekSchedule, weekDatesFor, type WeekEnrollment } from '../../utils/weekSchedule';
import { useFlexPlanDates } from '../../hooks/useFlexPlanDates';
import { MapPin } from 'lucide-react';

/**
 * Width of the left column holding the hours, in px.
 *
 * Half what it was, because `TimeLabel` puts the start and end on their own lines: the column
 * only has to hold `11:30 AM –`, never the whole range. The two lines cost no height — the bar
 * beside them is taller than both put together.
 *
 * From `lg` it goes back up, and the range goes back onto one line with it: the two-line form
 * exists to keep a *narrow* column narrow, and 96px holds `9:00 AM – 3:00 PM` whole.
 *
 * Published as a CSS custom property rather than passed around as a number, because three
 * separate rules — the header's left padding, each row's gutter width, and the scroll
 * container's minimum width — have to agree on it, and only CSS knows which breakpoint is in
 * force at the time. Both values live in the class string as literal text — Tailwind scans
 * source rather than evaluating it, and an inline `style` would outrank the `lg:` rule and pin
 * the column at its phone width at every size.
 */
const TIME_COL_CLASS = '[--week-time-col:72px] lg:[--week-time-col:96px]';

/** Reads whichever of the two the current breakpoint put there. */
const TIME_COL = 'var(--week-time-col)';

/** Narrowest a day column may get before the grid starts scrolling instead. */
const MIN_DAY_PX = 96;

const WEEKDAY_LABEL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * The rest of each weekday's name, revealed from `lg` where the column has room for it.
 *
 * A tail rather than a second full label: rendering `Mon` and `Monday` as a hidden pair would
 * put every weekday on the page twice — once for a screen reader to read out, once more for
 * any test asking what this column says.
 */
const WEEKDAY_TAIL = ['day', 'day', 'sday', 'nesday', 'rsday', 'day', 'urday'];

/** `days` is trimmed of quiet weekend columns, so column count is read off it, never assumed. */

/**
 * A time range, always on two lines: start above, end below.
 *
 * Always, not only when it runs out of room. Letting it wrap naturally gave a ragged column of
 * one- and two-line labels whose baselines never lined up, and broke wherever the text happened
 * to run out — `9:00 AM – 3:00` above an orphaned `PM`. Fixing the shape also shrinks the
 * column by half, because it now only has to hold one time at a time rather than two.
 *
 * A programme with no usable duration prints only its start, so there is nothing to split and
 * it stays on one line — the one case where a range does not exist to be shown.
 */
function TimeLabel({ label }: { label: string }) {
  const [start, end] = label.split(' – ');
  if (!end) return <span className="whitespace-nowrap">{label}</span>;
  return (
    <>
      <span className="block whitespace-nowrap lg:inline">{start} &ndash;</span>{' '}
      <span className="block whitespace-nowrap lg:inline">{end}</span>
    </>
  );
}

export function WeekCalendar({
  enrollments,
  className = '',
}: {
  enrollments: WeekEnrollment[];
  className?: string;
}) {
  const label = useLabelParts();
  const heading = label('header.thisWeek');
  const today = getTodaySessionDate();

  // Flex bills on a plan, not a cron, so its bars can only be drawn from the month plan —
  // the same source the Today card uses. Both months when the week straddles one. The query
  // is skipped entirely for the great majority of families, who hold no Flex seat.
  const weekDates = weekDatesFor(today);
  const flexEnrollmentIds = enrollments
    .filter(e => e.programs?.billing_type === 'flex_monthly')
    .map(e => e.id);
  const flexDates = useFlexPlanDates(flexEnrollmentIds, weekDates);

  const withPlans = enrollments.map(e =>
    flexDates[e.id] ? { ...e, selectedDays: flexDates[e.id] } : e,
  );
  const { days, rows, offWeek } = buildWeekSchedule(withPlans, today);

  return (
    <section className={`rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 lg:p-6 ${className}`} aria-label={heading.en}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 className={TYPE_SECTION_WIDE}>
          {heading.zh && <span className="mr-1.5 font-normal normal-case tracking-normal">{heading.zh}</span>}
          {heading.en}
        </h2>
        {/* The date range, and nothing else. Enrollment history used to hang off this header,
            where it was a navigation link wearing a timetable's clothes — it moved to the
            shortcuts block, which is where the page keeps the places you can go next. */}
        <span className={TYPE_SECTION_WIDE}>
          {formatDateDisplay(days[0].date)} – {formatDateDisplay(days[days.length - 1].date)}
        </span>
      </div>

      {rows.length === 0 ? (
        <p className={TYPE_ATTRIBUTE_WIDE}>No classes scheduled this week.</p>
      ) : (
        /* Seven columns plus the hour gutter need about 700px to stay legible, which a phone
           does not have. The grid scrolls inside its own box rather than pushing the page
           sideways. */
        <div className={`-mx-1 overflow-x-auto px-1 pb-1 ${TIME_COL_CLASS}`}>
          {/* Wide enough for the columns that survived the weekend trim, not a fixed seven. */}
          <div style={{ minWidth: `calc(${TIME_COL} + ${days.length * MIN_DAY_PX}px)` }}>
            <div className="flex" style={{ paddingLeft: TIME_COL }}>
              {days.map(day => (
                <div key={day.date} className="flex-1 pb-1 text-center lg:pb-2">
                  <div className={`text-[10px] font-semibold uppercase tracking-wider ${day.isToday ? 'text-[color:var(--brand-accent)]' : 'text-gray-400'}`}>
                    {WEEKDAY_LABEL[day.weekday]}
                    <span className="hidden lg:inline">{WEEKDAY_TAIL[day.weekday]}</span>
                  </div>
                  <div className={`text-xs tabular-nums lg:text-base ${day.isToday ? 'font-bold text-[color:var(--brand-accent)]' : 'text-gray-500'}`}>
                    {Number(day.date.slice(8))}
                  </div>
                  {/* Why a class is missing from this column. On the header rather than in the
                      empty cells, because one closure empties every row that day and would
                      otherwise repeat once per child. Amber, like the "No class on" list on
                      the enrollment page, where the same reason appears. */}
                  {day.closure && (
                    <div className="mt-0.5 px-1 text-[10px] font-medium leading-tight text-amber-700 lg:text-xs">
                      {day.closure}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {rows.map(row => {
              const who = row.attendee;

              return (
                <div key={row.key} className="flex border-t border-gray-100">
                  <div
                    className="flex flex-none items-center justify-end pr-2.5 text-right lg:pr-3"
                    style={{ width: TIME_COL }}
                  >
                    <span className="text-[10px] tabular-nums leading-tight text-gray-500 lg:text-[13px]">
                      <TimeLabel label={row.timeLabel} />
                    </span>
                  </div>

                  <div className="relative flex-1">
                    {/* Day columns are the ground: borders and today's tint. Nothing is
                        rendered inside them, because a bar can span several at once. */}
                    <div className="absolute inset-0 flex">
                      {days.map(day => (
                        <div
                          key={day.date}
                          className={`flex-1 border-l border-gray-100 last:border-r ${day.isToday ? 'bg-blue-50/60' : ''}`}
                        />
                      ))}
                    </div>

                    {/* A grid rather than absolute positioning: each bar is a grid item spanning
                        its own day columns, so the row grows to fit whichever bar had to wrap to
                        the most lines. Absolutely-positioned bars needed a height decided in
                        advance, which is what forced the programme name into an ellipsis. */}
                    <div
                      className="relative grid py-1 lg:py-1.5"
                      style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}
                    >
                    {row.bars.map(bar => {
                      const lastDay = bar.dayStart + bar.daySpan - 1;
                      const span = bar.daySpan === 1
                        ? WEEKDAY_LABEL[days[bar.dayStart].weekday]
                        : `${WEEKDAY_LABEL[days[bar.dayStart].weekday]}–${WEEKDAY_LABEL[days[lastDay].weekday]}`;
                      const location = row.locationName;
                      const detail = `${who.firstName} · ${row.programName} · ${span}${row.timeLabel ? ` · ${row.timeLabel}` : ''}${location ? ` · ${location}` : ''}`;
                      return (
                        <Link
                          key={`${row.key}-${bar.dayStart}`}
                          to={`/enrollments/${who.enrollmentId}`}
                          title={detail}
                          aria-label={detail}
                          className="mx-0.5 block min-h-[26px] overflow-hidden rounded border border-gray-200 border-l-[3px] bg-gray-50 px-1 py-1 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 lg:mx-1 lg:min-h-[52px] lg:rounded-md lg:px-2 lg:py-1.5"
                          style={{
                            gridColumn: `${bar.dayStart + 1} / span ${bar.daySpan}`,
                            gridRow: 1,
                            borderLeftColor: getProgramTypeColor(row.programType),
                            textDecoration: 'none',
                          }}
                        >
                          {/* An initial rather than a name: it survives a one-day bar, and it is
                              the colour the child already wears on their avatar. */}
                          {/* Wraps to at most three lines and may break inside a word — a
                              programme called "Recurring prog Long name Long name" is worth
                              reading in full more than it is worth one tidy line. Five lines is
                              the cap because the row grows to fit and a bar that tall starts
                              pushing the rest of the week off the screen.

                              The chip is inline *within* that text rather than a flex sibling
                              beside it, so a second line returns to the bar's left edge instead
                              of hanging indented under the first line's first word. */}
                          <span className="line-clamp-5 break-words text-[11px] font-semibold leading-tight lg:text-[13px] lg:leading-snug">
                            <span
                              className={`mr-1 inline-flex h-3.5 min-w-[15px] items-center justify-center rounded-sm px-0.5 align-middle text-[8px] font-bold leading-none text-white lg:mr-1.5 lg:h-[18px] lg:min-w-[20px] lg:text-[10px] ${solidTintFor(who.name)}`}
                            >
                              {initialsFor(who.name)}
                            </span>
                            {row.programName}
                          </span>
                          {location && (
                            /* Wraps rather than truncating: a one-day bar is too narrow for
                               "Annex Room 2", and an ellipsis made it indistinguishable from
                               "Annex Room 1" — the one part of the name that mattered. */
                            <span className="mt-0.5 flex min-w-0 items-start gap-0.5 text-[10px] leading-tight text-gray-500 lg:text-[11px]">
                              <MapPin className="mt-px h-2.5 w-2.5 flex-shrink-0 lg:h-3 lg:w-3" aria-hidden="true" />
                              <span className="min-w-0 break-words">{location}</span>
                            </span>
                          )}
                        </Link>
                      );
                    })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {offWeek.length > 0 && (
        /* An enrollment with no session this week still exists, and a calendar alone would
           make it look like it had been dropped. One quiet line, with the reason.

           Headed "Also enrolled" rather than "Not meeting this week", because three
           different states land here — a term that has not started, one that has finished,
           and one genuinely off this week — and only the last of those was ever "not meeting
           this week". A camp two weeks away was never going to meet this week, so listing it
           as an exception left the heading arguing with its own item. */
        <div className="mt-3 border-t border-gray-100 pt-2.5">
          <p className={`${TYPE_ATTRIBUTE_WIDE} font-medium`}>Also enrolled</p>
          <ul className="mt-1 space-y-0.5">
            {offWeek.map(item => (
              <li key={item.enrollmentId} className={TYPE_ATTRIBUTE_WIDE}>
                <Link
                  to={`/enrollments/${item.enrollmentId}`}
                  className="hover:underline"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  {item.programName} <span className="text-gray-400">· {item.note}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
